<script setup lang="ts">
  /**
   * Reset Password View
   *
   * Set new password using reset token.
   */
  import { ref, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { authService } from '../../services/auth.service'
  import { useNotifications } from '../../composables/useNotifications'

  const router = useRouter()
  const route = useRoute()
  const { showSuccess, showError } = useNotifications()

  const password = ref('')
  const confirmPassword = ref('')
  const loading = ref(false)
  const token = ref('')
  const tokenValid = ref(true)

  onMounted(() => {
    // Get token from URL query params
    token.value = (route.query.token as string) || ''
    if (!token.value) {
      tokenValid.value = false
    }
  })

  async function handleSubmit() {
    if (!password.value || !confirmPassword.value) {
      showError('Error', 'Please fill in all fields')
      return
    }

    if (password.value !== confirmPassword.value) {
      showError('Error', 'Passwords do not match')
      return
    }

    if (password.value.length < 8) {
      showError('Error', 'Password must be at least 8 characters')
      return
    }

    loading.value = true

    try {
      await authService.resetPassword({
        token: token.value,
        password: password.value,
      })
      showSuccess('Password Reset', 'Your password has been updated successfully')
      router.push('/login')
    } catch (error) {
      showError('Error', 'Failed to reset password. The link may have expired.')
      tokenValid.value = false
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div class="flex min-h-screen items-center justify-center bg-secondary-50 px-4">
    <div class="w-full max-w-md">
      <div class="rounded-xl bg-white p-8 shadow-lg">
        <!-- Invalid token state -->
        <template v-if="!tokenValid">
          <div class="text-center">
            <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger-50">
              <i class="pi pi-times text-3xl text-danger-600" />
            </div>
            <h2 class="text-lg font-semibold text-secondary-900">Invalid or Expired Link</h2>
            <p class="mt-2 text-secondary-600">
              This password reset link is invalid or has expired.
            </p>
            <RouterLink
              to="/forgot-password"
              class="mt-6 inline-block"
            >
              <PButton label="Request New Link" icon="pi pi-refresh" />
            </RouterLink>
          </div>
        </template>

        <!-- Reset form -->
        <template v-else>
          <div class="mb-8 text-center">
            <h1 class="text-2xl font-bold text-secondary-900">Set New Password</h1>
            <p class="mt-2 text-secondary-600">
              Enter your new password below.
            </p>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div>
              <label for="password" class="mb-2 block text-sm font-medium text-secondary-700">
                New Password
              </label>
              <PPassword
                id="password"
                v-model="password"
                toggleMask
                placeholder="Enter new password"
                class="w-full"
                inputClass="w-full"
                :disabled="loading"
              />
            </div>

            <div>
              <label for="confirmPassword" class="mb-2 block text-sm font-medium text-secondary-700">
                Confirm Password
              </label>
              <PPassword
                id="confirmPassword"
                v-model="confirmPassword"
                :feedback="false"
                toggleMask
                placeholder="Confirm new password"
                class="w-full"
                inputClass="w-full"
                :disabled="loading"
              />
            </div>

            <PButton
              type="submit"
              label="Reset Password"
              icon="pi pi-check"
              :loading="loading"
              class="w-full"
            />
          </form>

          <div class="mt-6 text-center">
            <RouterLink
              to="/login"
              class="text-sm text-secondary-600 hover:text-secondary-900"
            >
              <i class="pi pi-arrow-left mr-1" />
              Back to login
            </RouterLink>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
